import { useLayoutEffect } from 'react';

/**
 * Favicon used whenever neither the project type nor the survey style
 * provides one. The file must exist in `public/`.
 */
export const DEFAULT_FAVICON_FILE_NAME = 'favicon.ico';

/** Key used to share the resolved favicon between questionnaire pages. */
export const FAVICON_STORAGE_KEY = 'faviconPath';

/** Id of the `<link rel="shortcut icon">` tag declared in `public/index.html`. */
const FAVICON_ELEMENT_ID = 'favicon';

/**
 * Builds an absolute URL for a file served from the `public/` folder.
 */
export function getPublicFileUrl(fileName: string): string {
  const prefix = process.env.NODE_ENV === 'development' ? 'http' : 'https';

  return `${prefix}://${window.location.host}/${fileName}`;
}

/**
 * Resolves which favicon should be displayed, in this order:
 *
 * 1. `faviconFileName` - resolved by the API from `project.type`;
 * 2. `urlFavicon` - manually configured in the survey/project style;
 * 3. the default favicon.
 *
 * The API only returns `faviconFileName` for project types that have their own
 * branding, so surveys of any other type keep the favicon set in their style.
 */
export function resolveFaviconUrl(
  faviconFileName?: string | null,
  urlFavicon?: string | null,
): string {
  if (faviconFileName) {
    return getPublicFileUrl(faviconFileName);
  }

  if (urlFavicon) {
    return urlFavicon;
  }

  return getPublicFileUrl(DEFAULT_FAVICON_FILE_NAME);
}

/**
 * Updates the favicon tag.
 *
 * @param persist stores the URL so the pages rendered outside the
 * questionnaire data flow (e.g. the GDPR info text) can reuse it. Should stay
 * `false` for anything that is not survey branding.
 */
export function applyFavicon(faviconUrl: string, persist = true): void {
  const faviconElement = document.getElementById(FAVICON_ELEMENT_ID);

  faviconElement?.setAttribute('href', faviconUrl);

  if (!persist) {
    return;
  }

  try {
    localStorage.setItem(FAVICON_STORAGE_KEY, faviconUrl);
  } catch {
    // localStorage may be unavailable (private mode); the tag is already set.
  }
}

/**
 * Returns the favicon previously stored by another questionnaire page, or
 * `null` when there is none. It never falls back to the default favicon on
 * purpose: showing the wrong brand is worse than showing none, so the caller
 * should simply keep the neutral favicon declared in `public/index.html`.
 */
export function getStoredFaviconUrl(): string | null {
  try {
    return localStorage.getItem(FAVICON_STORAGE_KEY);
  } catch {
    return null;
  }
}

interface UseFaviconOptions {
  /** Favicon file name resolved by the API from the project type. */
  faviconFileName?: string | null;
  /** Favicon manually configured in the survey/project style. */
  urlFavicon?: string | null;
  /**
   * Whether the data needed to resolve the favicon is already loaded.
   * Defaults to `true`.
   */
  isReady?: boolean;
  /** Whether the resolved favicon should be stored. Defaults to `true`. */
  persist?: boolean;
}

/**
 * Keeps the page favicon in sync with the project type / survey style.
 *
 * While `isReady` is false nothing is touched, so the neutral favicon declared
 * in `public/index.html` stays visible until the survey data arrives. Applying
 * the default favicon earlier would make the icon change twice on every
 * branded survey, which is exactly what we want to avoid.
 *
 * @returns the favicon URL that was applied, or `undefined` while not ready.
 */
export default function useFavicon({
  faviconFileName,
  urlFavicon,
  isReady = true,
  persist = true,
}: UseFaviconOptions): string | undefined {
  const faviconUrl = isReady
    ? resolveFaviconUrl(faviconFileName, urlFavicon)
    : undefined;

  useLayoutEffect(() => {
    if (faviconUrl) {
      applyFavicon(faviconUrl, persist);
    }
  }, [faviconUrl, persist]);

  return faviconUrl;
}
